import type { VersionInfo } from "../../types";

const HIDDEN_KEYS = new Set(["Comments", "History", "platform", "specifier"]);

function mostRecentChange(data: VersionInfo): string | null {
  const history = data.History as Record<string, string> | undefined;
  if (!history || Object.keys(history).length === 0) return null;
  const latestKey = Object.keys(history).sort().reverse()[0];
  return `${latestKey}: ${history[latestKey]}`;
}

export function VersionPanel({
  title,
  data,
  diffAgainst,
  selected = false,
}: {
  title: string;
  data: VersionInfo;
  diffAgainst?: VersionInfo | null;
  selected?: boolean;
}) {
  const entries = Object.entries(data).filter(([key]) => !HIDDEN_KEYS.has(key));
  const recentChange = mostRecentChange(data);

  // Split entries into 2 columns, flowing down then right
  const COL_COUNT = 2;
  const rowCount = Math.ceil(entries.length / COL_COUNT);
  const columns = Array.from({ length: COL_COUNT }, (_, i) =>
    entries.slice(i * rowCount, (i + 1) * rowCount)
  );

  return (
    <div class={`version-panel${selected ? " version-panel-selected" : ""}`}>
      <div class="version-title">
        {title}. Platform: {String(data.platform ?? "")} | Specifier: {String(data.specifier ?? "")}
        {recentChange && <span class="version-recent-change"> ({recentChange})</span>}
      </div>
      <div class="version-cols">
        {columns.map((col, ci) => (
          <table key={ci}>
            <tbody>
              {col.map(([key, value]) => {
                const baseline = diffAgainst?.[key];
                // Cai/Postproc in the baseline panel can be a "option1 | option2" list
                // of every available choice rather than one resolved value.
                const isDiff = !!diffAgainst && (
                  typeof baseline === "string" && baseline.includes(" | ")
                    ? !baseline.split(" | ").includes(String(value))
                    : baseline !== value
                );
                return (
                  <tr key={key}>
                    <td class="version-col-key" title={key}>{key}</td>
                    <td class={`version-col-value${isDiff ? " diff" : ""}`}>{String(value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}
